import type {
  DeviceStatus,
  ExecutionStatus,
  FlowStatus,
  ManufacturingOrderStatus,
  OrderStatus,
  QCResult,
} from "@/lib/types";

type Tone = "neutral" | "ok" | "warn" | "danger" | "info" | "accent";

const ORDER_TONE: Record<OrderStatus, Tone> = {
  DRAFT: "neutral",
  ACTIVE: "accent",
  ADJUSTED: "warn",
  COMPLETED: "ok",
  CANCELLED: "neutral",
};

const MO_TONE: Record<ManufacturingOrderStatus, Tone> = {
  DRAFT: "neutral",
  READY: "info",
  IN_PRODUCTION: "accent",
  COMPLETED: "ok",
  CANCELLED: "neutral",
};

const EXEC_TONE: Record<ExecutionStatus, Tone> = {
  STARTED: "info",
  IN_PROGRESS: "info",
  QC_PENDING: "warn",
  COMPLETED: "ok",
  FAILED: "danger",
};

const FLOW_TONE: Record<FlowStatus, Tone> = {
  PENDING: "neutral",
  IN_PROGRESS: "info",
  ACCEPTED: "ok",
  REJECTED: "danger",
  DISPOSED: "danger",
};

const QC_TONE: Record<QCResult, Tone> = {
  ACCEPTED: "ok",
  REJECTED: "danger",
};

const DEVICE_TONE: Record<DeviceStatus, Tone> = {
  ONLINE: "ok",
  OFFLINE: "neutral",
  ERROR: "danger",
};

const TONE_CLASS: Record<Tone, string> = {
  neutral: "pill",
  ok: "pill pill-ok",
  warn: "pill pill-warn",
  danger: "pill pill-danger",
  info: "pill pill-info",
  accent: "pill pill-accent",
};

interface Props {
  value: string;
  kind?:
    | "order"
    | "manufacturing"
    | "execution"
    | "flow"
    | "qc"
    | "device"
    | "custom";
  tone?: Tone;
}

export function StatusPill({ value, kind = "custom", tone }: Props) {
  const resolvedTone: Tone =
    tone ??
    (kind === "order"
      ? ORDER_TONE[value as OrderStatus] ?? "neutral"
      : kind === "manufacturing"
      ? MO_TONE[value as ManufacturingOrderStatus] ?? "neutral"
      : kind === "execution"
      ? EXEC_TONE[value as ExecutionStatus] ?? "neutral"
      : kind === "flow"
      ? FLOW_TONE[value as FlowStatus] ?? "neutral"
      : kind === "qc"
      ? QC_TONE[value as QCResult] ?? "neutral"
      : kind === "device"
      ? DEVICE_TONE[value as DeviceStatus] ?? "neutral"
      : "neutral");
  return <span className={TONE_CLASS[resolvedTone]}>{value}</span>;
}
